import React, { useState } from 'react';
import axios from 'axios';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface FeedbackBubbleProps {
  userId: string;
  authToken: string;
  firstName: string;
  lastName: string;
  email: string;
  memberNumber?: number;
  onShowGuide?: () => void;
}

const FeedbackBubble: React.FC<FeedbackBubbleProps> = ({
  userId, authToken, firstName, lastName, email, memberNumber, onShowGuide,
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/feedback`, {
        message, userId, firstName, lastName, email, memberNumber,
      }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSent(true);
      setMessage('');
      setTimeout(() => { setSent(false); setOpen(false); }, 2500);
    } catch {
      // silent fail — user can try again
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating ? button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-20 right-4 md:bottom-6 z-50 w-10 h-10 rounded-full bg-charcoal text-cream flex items-center justify-center shadow-lg hover:bg-charcoal-light transition-colors"
        aria-label="Feedback"
      >
        <span className="font-serif text-lg leading-none">?</span>
      </button>

      {/* Popup panel */}
      {open && (
        <div className="fixed bottom-32 right-4 md:bottom-20 z-50 w-72 bg-white rounded-xl shadow-xl border border-cream-dark overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-start p-4 pb-3 border-b border-cream-dark">
            <div>
              <p className="font-serif text-base text-charcoal leading-tight">Help me please!</p>
              <p className="text-xs text-stone-light mt-0.5">We read every message</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-stone-light hover:text-charcoal transition-colors ml-3 mt-0.5 flex-shrink-0"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-4">
            {sent ? (
              <div className="text-center py-4">
                <p className="font-serif text-charcoal mb-1">Thank you!</p>
                <p className="text-xs text-stone-light">We'll be in touch if needed.</p>
              </div>
            ) : (
              <>
                {onShowGuide && (
                  <button
                    onClick={() => { setOpen(false); onShowGuide(); }}
                    className="w-full flex items-center justify-between mb-4 pb-4 border-b border-cream-dark group"
                  >
                    <div className="text-left">
                      <p className="text-sm text-charcoal font-medium group-hover:text-stone transition-colors">How to use Scentd</p>
                      <p className="text-xs text-stone-light mt-0.5">Walk through the app guide →</p>
                    </div>
                    <span className="text-stone-light group-hover:text-charcoal transition-colors text-lg leading-none ml-2">›</span>
                  </button>
                )}
                <p className="text-xs text-stone-light tracking-widest uppercase mb-2">Message us</p>
                <p className="text-xs text-stone leading-relaxed mb-3">We read all of them.</p>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Feedback, questions, anything..."
                  rows={4}
                  className="w-full border border-cream-dark rounded-xl px-3 py-2.5 text-sm text-charcoal placeholder-stone-light resize-none focus:outline-none focus:border-stone transition-colors bg-cream"
                />
                <button
                  onClick={handleSubmit}
                  disabled={sending || !message.trim()}
                  className="w-full mt-3 bg-charcoal text-cream py-2.5 text-xs tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-40"
                >
                  {sending ? 'Sending...' : 'Send message'}
                </button>
                <p className="text-center mt-3">
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-stone-light text-xs hover:text-stone transition-colors">
                    Privacy policy
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackBubble;
